import { Injectable } from '@angular/core';
import { formatISO } from 'date-fns';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { LoginResponse } from '../enums/login.response.enum';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { ServerInfo } from '../server/serverinfo.model';
import { SystemChangesService } from '../system-changes/system-changes.service';
import { WebSocketGenericMessage, WebSocketMessage, WebSocketTypeEnum } from './websocket.types';
import { PremisesServices } from '../../modules/admin/projects/premises-management/premises.services';
import { TasksService } from '../../modules/admin/tasks/tasks.service';
import { GlobalTasksService } from '../../modules/admin/global-tasks/tasks.service';
import { WorkshiftsService } from '../../modules/admin/workshifts/workshifts.service';
import { TaskListService } from '../../modules/admin/task-list/task-list.service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {

    private newGenericMessage = new Subject<WebSocketGenericMessage>();
    private genericMessages = new BehaviorSubject<WebSocketGenericMessage[]>([]);

    private serverInfo: ServerInfo;
    private webSocket: WebSocket;
    private opened: boolean = false;
    private closed: boolean = true;
    private websocketServerURL: string;
    private accessToken: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private authService: AuthService,
        private premisesService: PremisesServices,
        private systemChangesService: SystemChangesService,
        private tasksService: TasksService,
        private globalTasksService: GlobalTasksService,
        private workshiftsService: WorkshiftsService,
        private taskListService: TaskListService
    ) {
        const stringMessages = localStorage.getItem(StorageKeysEnum.genericMessages);
        if (stringMessages) {
            this.genericMessages.next(JSON.parse(stringMessages));
        } else {
            this.genericMessages.next([]);
        }
        this.authService.deleteGenericMessages
            .subscribe(() => this.setGenericMessages([]));
    }

    get genericMessages$() {
        return this.genericMessages.asObservable();
    }

    get newGenericMessage$() {
        return this.newGenericMessage.asObservable();
    }

    setGenericMessages(genericMessages: WebSocketGenericMessage[]) {
        this.genericMessages.next(genericMessages);
        localStorage.setItem(StorageKeysEnum.genericMessages, JSON.stringify(genericMessages));
    }

    awaitServerInfo() {
        this.authService.serverInfo.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe((serverInfo) => {
            if (serverInfo) {
                this.serverInfo = serverInfo;
                this.connect();
            }
        });
    }
    async connect() {
        // open the connection if one does not exist
        if (this.webSocket !== undefined
            && this.webSocket.readyState !== WebSocket.CLOSED) {
            return;
        }
        this.websocketServerURL = this.serverInfo?.websocketServerURL;
        if (!this.authService.accessTokenIsValid()) {
            if (this.authService.refreshTokenIsValid()) {
                try {
                    await firstValueFrom(this.authService.doRefreshToken());
                } catch (error) {
                    console.error(error);
                    return;
                }
            } else {
                return;
            }
        }
        this.accessToken = this.authService.accessToken;
        const wsUrl = this.websocketServerURL + '/' + encodeURI(environment.applicationId) + '/' + encodeURI(this.accessToken);

        const textDecode = new TextDecoder('utf-8');

        // Create a websocket
        this.webSocket = new WebSocket(wsUrl);
        this.webSocket.binaryType = 'arraybuffer';

        this.webSocket.onopen = (event) => {
            this.opened = true;
            this.closed = false;
        };

        this.webSocket.onmessage = (event) => {
            if (event.data instanceof ArrayBuffer) {
                const msg: WebSocketMessage = JSON.parse(textDecode.decode(event.data));
                switch (msg.type) {
                    case WebSocketTypeEnum.TASK_ROW_UPDATE:
                        this.tasksService.setWebSocketMessage(msg);
                        this.globalTasksService.setWebSocketMessage(msg);
                        break;
                    case WebSocketTypeEnum.WORKSHIFT_ROW_UPDATE:
                        this.workshiftsService.handleWebSocketMessage(msg);
                        break;
                    case WebSocketTypeEnum.WORKSHIFT_USER_EVENT_UPDATE:
                        this.workshiftsService.setWorkshiftUserEventUpdateMsg(msg.payload);
                        break;
                    case WebSocketTypeEnum.GENERIC_ALERT:
                        const messages = this.genericMessages.value;
                        const newMessage: WebSocketGenericMessage = { ...msg, receivedDate: formatISO(new Date()) };
                        messages.unshift(newMessage);
                        this.setGenericMessages(messages);
                        this.newGenericMessage.next(newMessage);
                        break;
                    case WebSocketTypeEnum.SYSTEM_CHANGES:
                        this.authService.isLoggedIn().then(async loggedIn => {
                            if (loggedIn === LoginResponse.LOGIN_SUCCESS) {
                                this.systemChangesService.retrieveSystemChangesForUsers();
                            }
                        });
                        break;
                    case WebSocketTypeEnum.DAILY_TASK_UPDATE:
                    case WebSocketTypeEnum.PREMISES_HOTEL_DAILY_UPDATE:
                    case WebSocketTypeEnum.PREMISES_HOTEL_WORK_UPDATE:
                    case WebSocketTypeEnum.PREMISES_CENTER_WORK_UPDATE:
                    case WebSocketTypeEnum.PREMISES_GENERIC_WORK_UPDATE:
                    case WebSocketTypeEnum.PREMISES_GENERIC_ACTIVITY_TYPE_OBJECTIVE_LIST_UPDATE:
                        this.premisesService.handleWebSocketMessage(msg);
                        break;
                    case WebSocketTypeEnum.WORKPLACE_BILLING_AUTOMATION_CONFIG:
                        this.taskListService.handleAutomationConfigWebsocketMessage(msg);
                }
            }
        };

        this.webSocket.onclose = (event) => {
            this.opened = false;
            this.closed = true;
            this.connect();
        };
    }
}
